import { useEffect, useMemo, useRef } from 'react'

import { useQuery } from '@apollo/client/react'
import type { UniqueIdentifier } from '@dnd-kit/core'
import * as Sentry from '@sentry/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useQueryParam } from '~publish/hooks/useQueryParam'

import { IdeaCard_Idea } from '../../IdeaCard/fragments/IdeaCard'
import type { IdeaCardIdea } from '../../IdeaCard/fragments/IdeaCard'
import { buildItemsObject } from '../helpers/helpers'
import { logError } from '~publish/legacy/utils/logError'

const IDEAS_PER_PAGE = 1000
const MAX_IDEAS = 6000

export const GetIdeasBoard = graphql(
  /* GraphQL */ `
    query GetIdeasBoard(
      $organizationId: ID!
      $ideasOrganizationId: OrganizationId!
      $first: Int!
      $after: String
    ) {
      ideaGroups(input: { organizationId: $organizationId }) {
        id
        name
        isLocked
      }
      ideas(
        input: { organizationId: $ideasOrganizationId }
        first: $first
        after: $after
      ) {
        edges {
          node {
            id
            ...IdeaCard_Idea
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [IdeaCard_Idea],
)

type ItemsMap = Record<UniqueIdentifier, UniqueIdentifier[]>
type BoardData = {
  data: {
    ideas: IdeaCardIdea[]
    groups: { id: string; name: string; isLocked: boolean }[]
    itemsMap: ItemsMap
  }
  loading: boolean
  error: Error | undefined
  hasData: boolean
}

type FallbackRef = {
  orgId: string
  data: ResultOf<typeof GetIdeasBoard>
}

export function useBoardData(): BoardData {
  const currentOrganization = useCurrentOrganization()
  const [selectedTagIds] = useQueryParam<string[]>('tagIds')
  const isFetchingMore = useRef(false)
  // Retains the last complete result so the board keeps rendering during
  // background refetches or transient cache gaps (CORE-6173). Tagged with the
  // org id so a switch to a different org never shows stale data.
  const lastCompleteRef = useRef<FallbackRef | null>(null)

  const { data, dataState, loading, error, fetchMore } = useQuery(
    GetIdeasBoard,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error - organizationId is not always defined we skip if it is not defined
        organizationId: currentOrganization?.id,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error - organizationId is not always defined we skip if it is not defined
        ideasOrganizationId: currentOrganization?.id,
        first: IDEAS_PER_PAGE,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !currentOrganization?.id,
    },
  )

  // Live complete data from the current query response
  const liveCompleteData =
    dataState === 'complete' && data !== undefined ? data : undefined

  // Keep the fallback ref up-to-date (after commit) whenever fresh complete
  // data arrives for the current org, and drop it when the org changes. A
  // not-yet-cleared snapshot is never served: the fallback read below guards
  // on the org id.
  useEffect(() => {
    if (
      liveCompleteData !== undefined &&
      currentOrganization?.id !== undefined
    ) {
      lastCompleteRef.current = {
        orgId: currentOrganization.id,
        data: liveCompleteData,
      }
    } else if (
      lastCompleteRef.current !== null &&
      lastCompleteRef.current.orgId !== currentOrganization?.id
    ) {
      lastCompleteRef.current = null
    }
  }, [liveCompleteData, currentOrganization?.id])

  // Use live data first; fall back to the same-org snapshot during refetches
  // or transient cache gaps so columns/ideas never disappear mid-flight.
  const fallback =
    lastCompleteRef.current?.orgId === currentOrganization?.id
      ? lastCompleteRef.current?.data
      : undefined
  const completeData = liveCompleteData ?? fallback

  // Auto-pagination: guard on liveCompleteData (not the fallback) so we never
  // call fetchMore based on stale pageInfo while a network refetch is in flight.
  useEffect(() => {
    if (!liveCompleteData || isFetchingMore.current) return

    const { hasNextPage, endCursor } = liveCompleteData.ideas.pageInfo
    const loadedCount = liveCompleteData.ideas.edges.length

    if (!hasNextPage) return

    if (loadedCount >= MAX_IDEAS) {
      Sentry.captureMessage(
        `Ideas board: user has more than ${MAX_IDEAS} ideas`,
        {
          level: 'warning',
          extra: {
            organizationId: currentOrganization?.id,
            loadedCount,
          },
        },
      )
      return
    }

    isFetchingMore.current = true
    void fetchMore({ variables: { after: endCursor } })
      .catch(logError)
      .finally(() => {
        isFetchingMore.current = false
      })
  }, [liveCompleteData, fetchMore, currentOrganization?.id])

  const { ideas, itemsMap } = useMemo(() => {
    if (!error && completeData) {
      return buildItemsObject(completeData, selectedTagIds ?? [])
    }

    return { ideas: [], itemsMap: {} }
  }, [completeData, error, selectedTagIds])

  return {
    data: {
      itemsMap,
      ideas,
      groups: completeData?.ideaGroups ?? [],
    },
    loading,
    error,
    hasData: completeData !== undefined,
  }
}
